import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ensureNamespaceLoaded } from "../i18n/lazyNamespaces";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EtapaPipeline from "./EtapaPipeline";
import EtapaSolicitacoes from "./EtapaSolicitacoes";
import EtapaProdutos from "./EtapaProdutos";
import EtapaClientesNiveis from "./EtapaClientesNiveis";
import EtapaCargos from "./EtapaCargos";
import EtapaUsuarios from "./EtapaUsuarios";
import EtapaCanais from "./EtapaCanais";

const OPERATE_BLOCKS = ["pipeline", "requests", "journey", "channels"];
const JOBS = ["team", "channel", "operate"];

/**
 * Passo 2: a pessoa escolhe o job (time / canal / operação) e só vê o que aquele job pede.
 */
const EtapaComoAtende = ({
  moreOpen,
  onMoreOpenChange,
  isModelWorkspace,
  serveJob,
  onServeJobChange,
  pipelineProps,
  requestsProps,
  productsProps,
  journeyProps,
  rolesProps,
  usersProps,
  channelsProps,
}) => {
  const { t } = useTranslation("onboarding");
  const [operateIndex, setOperateIndex] = useState(0);
  useEffect(() => {
    void ensureNamespaceLoaded("onboarding");
  }, []);

  const job = serveJob || null;
  const blockKey = OPERATE_BLOCKS[Math.min(operateIndex, OPERATE_BLOCKS.length - 1)];

  const pickJob = (next) => {
    onServeJobChange?.(next);
    if (next === "operate") setOperateIndex(0);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {t("setup.howYouServe.title")}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
        {t("setup.howYouServe.intro")}
      </Typography>
      {isModelWorkspace ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("setup.howYouServe.modelHint")}
        </Alert>
      ) : null}

      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        {t("setup.howYouServe.jobsTitle")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
        {t("setup.howYouServe.jobsLead")}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
          gap: 2,
          mb: 3,
        }}
      >
        {JOBS.map((key) => {
          const selected = job === key;
          return (
            <Card
              key={key}
              variant="outlined"
              sx={{
                borderColor: selected ? "primary.main" : "divider",
                borderWidth: selected ? 2 : 1,
                bgcolor: selected ? "action.selected" : "background.paper",
              }}
            >
              <CardActionArea onClick={() => pickJob(key)} sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    {t(`setup.howYouServe.jobs.${key}.title`)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t(`setup.howYouServe.jobs.${key}.body`)}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>

      {job === "team" ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t("setup.howYouServe.teamConfirm")}
        </Alert>
      ) : null}

      {job === "team" ? (
        <Button variant="text" onClick={() => pickJob("operate")} sx={{ mb: 2 }}>
          {t("setup.howYouServe.teamReview")}
        </Button>
      ) : null}

      {job === "channel" && channelsProps ? (
        <Box sx={{ mt: 1 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("setup.howYouServe.channelsLead")}
          </Alert>
          <EtapaCanais {...channelsProps} />
        </Box>
      ) : null}

      {job === "operate" ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("setup.howYouServe.operateProgress", {
              current: operateIndex + 1,
              total: OPERATE_BLOCKS.length,
            })}
            {" · "}
            {t(`setup.howYouServe.blocks.${blockKey}`)}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {OPERATE_BLOCKS.map((key, idx) => (
              <Button
                key={key}
                size="small"
                variant={idx === operateIndex ? "contained" : "outlined"}
                onClick={() => setOperateIndex(idx)}
              >
                {t(`setup.howYouServe.blocks.${key}`)}
              </Button>
            ))}
          </Box>

          {blockKey === "pipeline" ? <EtapaPipeline {...pipelineProps} /> : null}
          {blockKey === "requests" ? <EtapaSolicitacoes {...requestsProps} /> : null}
          {blockKey === "journey" && journeyProps ? <EtapaClientesNiveis {...journeyProps} /> : null}
          {blockKey === "channels" && channelsProps ? (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                {t("setup.howYouServe.channelsLead")}
              </Alert>
              <EtapaCanais {...channelsProps} />
            </Box>
          ) : null}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3, gap: 2 }}>
            <Button
              onClick={() => setOperateIndex((i) => Math.max(0, i - 1))}
              disabled={operateIndex === 0}
            >
              {t("setup.howYouServe.operatePrev")}
            </Button>
            {operateIndex < OPERATE_BLOCKS.length - 1 ? (
              <Button variant="outlined" onClick={() => setOperateIndex((i) => i + 1)}>
                {t("setup.howYouServe.operateNext")}
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
                {t("setup.howYouServe.operateDone")}
              </Typography>
            )}
          </Box>
        </Box>
      ) : null}

      <Accordion
        expanded={moreOpen}
        onChange={(_, expanded) => onMoreOpenChange?.(expanded)}
        sx={{
          mt: 3,
          boxShadow: "none",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {t("setup.howYouServe.moreTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("setup.howYouServe.moreLead")}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {productsProps ? (
            <Box sx={{ mb: 4 }}>
              <EtapaProdutos {...productsProps} />
            </Box>
          ) : null}
          {rolesProps ? (
            <Box sx={{ mb: 4 }}>
              <EtapaCargos {...rolesProps} />
            </Box>
          ) : null}
          {usersProps ? (
            <Box sx={{ mb: 2 }}>
              <EtapaUsuarios {...usersProps} />
            </Box>
          ) : null}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default EtapaComoAtende;
